import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Card,
  message,
  Input,
} from 'antd';
import Table from 'components/Table';
import Separador from 'components/Separador';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { isMutable } from 'utils/estadosGlobales';
import { getModelPermission } from 'utils/permission/index';
import {
  getConfiguracionesDeSegmentosDeFolios,
  postConfiguracionesDeSegmentosDeFolios,
  patchConfiguracionesDeSegmentosDeFolios,
  deleteConfiguracionesDeSegmentosDeFolios,
  baseURI,
} from 'api/configuracion/configuracionesDeFolios';
import { postSegmentosDeFolios } from 'api/configuracion/segmentosDeFolios';
import { capitalize } from 'utils/normalizers';
import SegmentosFoliador, { model as modelSegmentos, catalogsModels as catalogsModelsSegmentos } from './SegmentosFoliador';

// eslint-disable-next-line react/prop-types
const Foliadores = ({ permission }) => {
  const [form] = Form.useForm();
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [segmentos, setSegmentos] = useState([]);
  const [permissionSegmentos, setPermissionSegmentos] = useState(null);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getConfiguracionesDeSegmentosDeFolios();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const permissionSegs = getModelPermission(modelSegmentos, catalogsModelsSegmentos);
      if (!permissionSegs.hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setPermissionSegmentos(permissionSegs);
        setHasAllViewPermission(permissionSegs.hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const _data = await getConfiguracionesDeSegmentosDeFolios();
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
  };

  const postSegmentos = async (id) => {
    if (segmentos.length && id) {
      await postSegmentosDeFolios(segmentos.map((e) => ({
        ...e,
        configuracion_de_folio: id,
      })));
    }
  };

  const onClickEdit = (response = null) => {
    const [key] = selectedRowKeys;
    const match = response || data.find((e) => e.id === key);
    form.setFieldsValue(match);
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const hasConsecutivo = segmentos.some((s) => s.tipo_de_segmento === 1);
      const [key] = selectedRowKeys;
      if (!key && !hasConsecutivo) {
        setLoading(false);
        return message.warn('Debe agregar al menos un segmento consecutivo');
      }
      const _data = await (key
        ? patchConfiguracionesDeSegmentosDeFolios(key, values)
        : postConfiguracionesDeSegmentosDeFolios(values));
      if (_data) {
        if (!key) {
          await postSegmentos(_data.id);
        }
        if (_continue) {
          setSelectedRowKeys([_data.id]);
          onClickEdit(_data);
        } else {
          onCancel();
        }
        fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
    return null;
  };

  const deleteItem = async () => {
    if (mutable) {
      const [key] = selectedRowKeys;
      const deleted = await deleteConfiguracionesDeSegmentosDeFolios(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    if (mutable) {
      setVisibleAlert(true);
    } else {
      message.info('No se puede eliminar un Foliador autorizado');
      onCancel();
    }
  };

  const columns = [
    {
      titleText: 'Separador',
      dataIndex: 'separador',
      key: 'separador',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 150,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estadosGlobales.find((t) => t.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            loading={loading}
            mobileColIndex={0}
            rowKey="id"
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Foliador`}
            extra={(
              <FormSubmitControls
                onFinish={(_constinue = false) => onFinish(_constinue)}
                onCancel={onCancel}
                mutable={mutable}
                loading={loading}
                permission={permission}
                allowAuthorize
                allowCancel
                allowSaveAndContinue
                selectedRowKeys={selectedRowKeys}
                baseURI={baseURI}
                callback={(estados_globales) => {
                  const [key] = selectedRowKeys;
                  const normalized = [...data]
                    .map((e) => (e.id === key ? { ...e, estados_globales } : e));
                  setMutable(false);
                  setData(normalized);
                  const match = normalized.find((e) => e.id === key);
                  onClickEdit(match);
                }}
              />
            )}
            bordered={false}
          >
            <Col span={24}>
              <Form
                layout="vertical"
                form={form}
                onFinish={() => onFinish()}
                scrollToFirstError
                initialValues={{ estados_globales: 1, separador: '-' }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="descripcion"
                      rules={rules.required}
                      label="Descripción"
                      hasFeedback
                      normalize={capitalize}
                    >
                      <Input allowClear disabled={!mutable} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Separador disabled={!mutable} rule={rules.required} />
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Form.Item
                      name="estados_globales"
                      rules={rules.required}
                      label="Estado"
                      hasFeedback
                    >
                      <Select
                        disabled={!selectedRowKeys.length || !mutable}
                        dataSource={estadosGlobales}
                      />
                    </Form.Item>
                  </Col>
                  {!!permissionSegmentos?.view && (
                    <Col span={24}>
                      <SegmentosFoliador
                        idConfiguracion={selectedRowKeys[0]}
                        setSegmentos={setSegmentos}
                        isMutable={mutable}
                        permission={permissionSegmentos}
                        hasAllViewPermission={hasAllViewPermission}
                      />
                    </Col>
                  )}
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Row>
              </Form>
            </Col>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content={`Foliador ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Foliadores;
